import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { generate945ForShipment, generate856ForShipment } from "@/lib/edi.functions";
import { runServerFn } from "@/lib/server-fn";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileCode } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/edi")({ component: EdiAdmin });

const blankAddr = { name: "", street: "", city: "", state: "", zip: "", country: "US" };

function EdiAdmin() {
  const g945 = useServerFn(generate945ForShipment);
  const g856 = useServerFn(generate856ForShipment);

  const [tx, setTx] = useState<"945" | "856">("945");
  const [shipmentId, setShipmentId] = useState("");
  const [senderId, setSenderId] = useState("WMSCO");
  const [receiverId, setReceiverId] = useState("PARTNER");
  const [ctl, setCtl] = useState(1);
  const [testFlag, setTestFlag] = useState<"T" | "P">("T");
  const [shipFrom, setShipFrom] = useState(blankAddr);
  const [shipTo, setShipTo] = useState(blankAddr);
  const [edi, setEdi] = useState("");

  const onGenerate = async () => {
    if (!shipmentId) { toast.error("Shipment ID required"); return; }
    try {
      const res = tx === "945"
        ? await runServerFn(() => g945({ data: { shipmentId, senderId, receiverId, controlNumber: ctl, testFlag } }), "Generate 945")
        : await runServerFn(() => g856({ data: { shipmentId, senderId, receiverId, controlNumber: ctl, testFlag, shipFrom, shipTo } }), "Generate 856");
      setEdi(res.edi);
      toast.success(`${tx} generated · ${res.edi.length} chars`);
    } catch (e: any) {
      toast.error(e.message ?? "Failed");
    }
  };

  const onDownload = () => {
    const blob = new Blob([edi], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tx}_${shipmentId.slice(0, 8)}_${ctl}.edi`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader title="EDI Generator" description="Generate X12 940/943/944/945/856 documents for enterprise partners." />
      <div className="px-4 sm:px-6 py-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Document</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Transaction</Label>
                <Select value={tx} onValueChange={(v) => setTx(v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="945">945 — Shipping Advice</SelectItem>
                    <SelectItem value="856">856 — ASN (multi-piece)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Test/Production</Label>
                <Select value={testFlag} onValueChange={(v) => setTestFlag(v as any)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="T">Test</SelectItem>
                    <SelectItem value="P">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Shipment ID</Label><Input value={shipmentId} onChange={(e) => setShipmentId(e.target.value)} placeholder="uuid" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div><Label className="text-xs">Sender ID</Label><Input value={senderId} onChange={(e) => setSenderId(e.target.value)} /></div>
              <div><Label className="text-xs">Receiver ID</Label><Input value={receiverId} onChange={(e) => setReceiverId(e.target.value)} /></div>
              <div><Label className="text-xs">Control #</Label><Input type="number" value={ctl} onChange={(e) => setCtl(Number(e.target.value) || 1)} /></div>
            </div>
            {tx === "856" && (
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                {(["shipFrom", "shipTo"] as const).map((side) => {
                  const val = side === "shipFrom" ? shipFrom : shipTo;
                  const set = side === "shipFrom" ? setShipFrom : setShipTo;
                  return (
                    <div key={side} className="space-y-2">
                      <div className="text-xs font-medium">{side === "shipFrom" ? "Ship from" : "Ship to"}</div>
                      <Input placeholder="Name" value={val.name} onChange={(e) => set({ ...val, name: e.target.value })} />
                      <Input placeholder="Street" value={val.street} onChange={(e) => set({ ...val, street: e.target.value })} />
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        <Input placeholder="City" value={val.city} onChange={(e) => set({ ...val, city: e.target.value })} />
                        <Input placeholder="ST" value={val.state} onChange={(e) => set({ ...val, state: e.target.value })} />
                        <Input placeholder="ZIP" value={val.zip} onChange={(e) => set({ ...val, zip: e.target.value })} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <Button onClick={onGenerate}><FileCode className="h-4 w-4 mr-1" /> Generate</Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Output</CardTitle>
            {edi && <Button size="sm" variant="outline" onClick={onDownload}><Download className="h-4 w-4 mr-1" /> Download</Button>}
          </CardHeader>
          <CardContent>
            {edi ? (
              <pre className="text-xs font-mono bg-muted/40 p-3 rounded max-h-[480px] overflow-auto whitespace-pre-wrap break-all">{edi}</pre>
            ) : (
              <div className="text-sm text-muted-foreground p-4 text-center">No document generated yet.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
